import React from 'react';
import {Tr, Th, Td, Button, Checkbox, IconButton, Box, Link} from '@chakra-ui/react';
import {CustomInput} from '../components/General/FormInputs';
import {FaArrowAltCircleUp, FaArrowAltCircleDown} from 'react-icons/fa';

// Will give you a header, body, footer in a state to use in your table
// Takes in:

// visibleData: data to show in table from json:
// ie.. const visibleData = useRef({firstName: 'text', lastName: 'text', isActive: 'boolean'});

// data: array of json with keys named exactly like the visible data ( and an id field ):
// ie..  const data = [{firstName:"chris", lastName:"tucker", isActive:true, id:1}, {firstName:"chris", lastName:"tucker", isActive:false, id:2}]

// pages: used to paginate grids and make subsequent requests for new pages, getNewPage is the function to reload data in whatever way you choose
// ie.. const [pages, setPages] = useState({pageNum: 1, totalPages: 5, itemsPerPage: 5, getNewPage:getNewPage});

// setElements: a state setter that will set the data (this function also returns if that's what you're into, but state is typically better for rendering)
// ie.. const [elements, setElements] = useState({body: {}, header: {}, footer:{}}); <--- pass the setElements then it will populate elements

// extraColumns(optional): Since these rows are mapped you can't add extra columns, If you need more custom columns or code in a row pass in jsx here
// ie.. const extraColumns = () => {
//   return(<>
//    <Td>
//      <IconButton colorScheme="tertiary" title="Edit" size="md" icon={<FaEdit />} mr="2" onClick={() => addEdit(user.id)} />
//      <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => setForDeletion(user.id, user.userName)} />
//    </Td>; </>)};

const setGrid = (visibleData, data, pages, setElements, ExtraColumns = null) => {
  const dataLength = data?.length;

  const Header = Object.keys(visibleData.current).map(function (key, index) {
    return <Th key={index}> {key} </Th>;
  });

  let Body = <></>;
  if (dataLength > 0) {
    Body = data.map((item, index) => {
      const cell = Object.entries(visibleData.current).map(function ([key, value], index) {
        if (value === 'text') {
          return (
            <Td key={key} textAlign="left">
              {item[key]}
            </Td>
          );
        } else if (value === 'boolean') {
          return (
            <Td key={key}>
              <Checkbox isReadOnly isChecked={item[key]} />
            </Td>
          );
        } else if (value === 'date') {
          const date = new Date(item[key]);
          let formatted = '';
          if (date == null || date == undefined || date == 'Invalid Date') formatted = 'N/A';
          else
            formatted =
              (date.getMonth() > 8 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1)) +
              '/' +
              (date.getDate() > 9 ? date.getDate() : '0' + date.getDate()) +
              '/' +
              date.getFullYear();

          return (
            <Td key={key} textAlign="left">
              {formatted}
            </Td>
          );
        } else if (value?.type === 'link') {
          return (
            <Td key={key} textAlign="left">
              <Link color="blue.600" textDecor="underline" onClick={() => value?.onClick(...value.params.map(p => item[p]))}>
                {item[key]}
              </Link>
            </Td>
          );
        }
      });

      return (
        <Tr key={item.id + 'tr' + index}>
          {cell}
          {ExtraColumns != null && (
            <ExtraColumns
              id={item.id}
              batch={item.batchId ?? ''}
              calias={item.calias ?? ''}
              idx={index}
              aliases={item.aliases?.length > 0 ? true : false}
              status={item.status ?? ''}
              sMaster={item.sMasterId ?? ''}
              fileError={item.fileError ?? ''}
              isDeleted={item.isDeleted ?? ''}
            />
          )}
        </Tr>
      );
    });
  }

  // May need to tweak the footer alignment depending on number of columns or odd/even data length
  const Footer = [];
  // Error in console about list not having a key so placing an arbitrary one here
  let keyVal = 0;
  const span = ExtraColumns != null && dataLength + ExtraColumns.length != 0 ? dataLength + ExtraColumns?.length : 0;
  Footer.push(
    <Td colSpan={!isNaN(span) ? span : 0} textAlign={'center'} key={keyVal++}>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === 1 ? true : false}
        isDisabled={pages.pageNum === 1 ? true : false}
        onClick={() => pages.getNewPage(1)}
      >
        {'<<'}
      </Button>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === 1 ? true : false}
        isDisabled={pages.pageNum === 1 ? true : false}
        onClick={() => pages.getNewPage(--pages.pageNum)}
      >
        {'<'}
      </Button>
      {pages.pageNum}
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === pages.totalPages ? true : false}
        isDisabled={pages.pageNum === pages.totalPages ? true : false}
        onClick={() => pages.getNewPage(++pages.pageNum)}
      >
        {'>'}
      </Button>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === pages.totalPages ? true : false}
        isDisabled={pages.pageNum === pages.totalPages ? true : false}
        onClick={() => pages.getNewPage(pages.totalPages)}
      >
        {'>>'}
      </Button>
    </Td>
  );
  const elements = {header: Header, body: Body, footer: Footer};
  setElements(elements);
  return elements;
};

// Pass in the IDs that should be sent when the form is submitted (likely when a user searches),
// state setter for the values on screen,
// the useform things - register and errors
// then an ascFunc and descFunc, if they exist we'll show sort buttons and these functions will ascend and descend the data
const setSearchHeaders = (searchIds, setHeaders, register, errors, ascFunc = null, descFunc = null) => {
  const Header = Object.keys(searchIds.current).map(function (key, index) {
    return (
      <Td key={index} mx="" px="1">
        <Box>
          <CustomInput
            register={register}
            id={key}
            name={key.replace(/([A-Z])/g, ' $1').trim()}
            errors={errors}
            requireInt={searchIds.current[key] === 'number'}
            datePicker={searchIds.current[key] === 'date'}
          />
          {descFunc != null && ascFunc != null && (
            <>
              <IconButton
                colorScheme="tertiary"
                icon={<FaArrowAltCircleDown />}
                title="Sort Ascending"
                ml="4"
                mt="3"
                onClick={() => {
                  ascFunc(key);
                }}
              />
              <IconButton
                colorScheme="tertiary"
                icon={<FaArrowAltCircleUp />}
                title="Sort Descending"
                ml="4"
                mt="3"
                onClick={() => {
                  descFunc(key);
                }}
              />
            </>
          )}
        </Box>
      </Td>
    );
  });

  setHeaders(Header);
};

const PageButtons = ({pages}) => {
  return (
    <>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === 1 ? true : false}
        isDisabled={pages.pageNum === 1 ? true : false}
        onClick={() => pages.getNewPage(1)}
      >
        {'<<'}
      </Button>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === 1 ? true : false}
        isDisabled={pages.pageNum === 1 ? true : false}
        onClick={() => pages.getNewPage(--pages.pageNum)}
      >
        {'<'}
      </Button>
      {pages.pageNum}
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === pages.totalPages ? true : false}
        isDisabled={pages.pageNum === pages.totalPages ? true : false}
        onClick={() => pages.getNewPage(++pages.pageNum)}
      >
        {'>'}
      </Button>
      <Button
        variant={'link'}
        colorScheme="mint"
        isActive={pages.pageNum === pages.totalPages ? true : false}
        isDisabled={pages.pageNum === pages.totalPages ? true : false}
        onClick={() => pages.getNewPage(pages.totalPages)}
      >
        {'>>'}
      </Button>
    </>
  );
};

export {setGrid, setSearchHeaders, PageButtons};
